import * as _ from 'lodash';
import * as moment from 'moment';
import { Component } from 'react';
import { Dispatch, connect } from 'react-redux';
import { IAppState, AppActionCreators, IApplicationState, ReportActionCreators, IDashMonitoringResponse } from '@nkr/store/index';
import { DisplayType } from '@nkr/enums/index';
import { IRefreshInterval } from '@nkr/views/swap-management/swaps/models';
import { FormControlWrapper } from '@nkr/views/swap-management/swaps/styles';
import { Glyphicon, FormControl, Row, Col } from 'react-bootstrap';
import { IDashMonitoringCount } from '@nkr/models/index';
import { Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Line, ComposedChart, Label, Text, ResponsiveContainer } from 'recharts';
import CustomizedAxisTick from './customized-axis-tick';
import CustomizedLabelLeft from './customized-label-left';

const refreshIntervals: IRefreshInterval[] = [
    { value: 0, label: 'Disable' },
    { value: 5, label: '5 seconds' },
    { value: 30, label: '30 seconds' },
    { value: 45, label: '45 seconds' },
    { value: 60, label: '60 seconds' }
];

interface IOwnProps { }

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getDashMonitoring: (startDate: string, endDate: string) => Promise<IDashMonitoringResponse>,
    changeDisplay: (display: number) => void
}

type TProps = IStateProps & IDispatchProps & IOwnProps;

interface IState {
    loading: boolean,
    autoRefresh: number,
    startDate: string,
    endDate: string,
    registrations: IDashMonitoringCount[],
    referrals: IDashMonitoringCount[],
    donorConnectRequests: IDashMonitoringCount[],
    donorConnectContactsMade: IDashMonitoringCount[],
    labOrders: IDashMonitoringCount[],
    labResults: IDashMonitoringCount[]
}

class DashMonitoring extends Component<TProps, IState> {
    readonly state: IState = {
        loading: false,
        autoRefresh: 0,
        startDate: moment().subtract(9, 'day').format('MM/DD/YYYY'),
        endDate: moment().format('MM/DD/YYYY HH:mm'),
        registrations: [],
        referrals: [],
        donorConnectRequests: [],
        donorConnectContactsMade: [],
        labOrders: [],
        labResults: []
    }

    componentDidMount = () => {
        this.getData();
    }

    handleDisplayChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.props.changeDisplay(parseInt(e.currentTarget.value));
    }

    handleRefresh = () => {
        this.refresh();
    }

    handleAutoRefreshChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            autoRefresh: parseInt(e.currentTarget.value)
        }, () => {
            if (this.state.autoRefresh > 0) {
                setTimeout(() => this.refresh(), this.state.autoRefresh * 1000);
            }
        });
    }

    refresh = () => {
        if (__ENVIRONMENT__ == 'dev') {
            let interval = _.random(50, 100);
            let startDate = moment().subtract(interval, 'day').format('MM/DD/YYYY');
            let endDate = moment().subtract(interval - 10, 'day').format('MM/DD/YYYY HH:mm');

            this.setState({
                startDate,
                endDate,
                loading: true
            }, () => {
                this.getDashMonitoring()
                    .then(() => {
                        this.setState({
                            loading: false
                        }, () => {
                            if (this.state.autoRefresh > 0) {
                                setTimeout(() => this.refresh(), this.state.autoRefresh * 1000);
                            }
                        });
                    })
                    .catch(error => {
                        if (this.state.autoRefresh > 0) {
                            setTimeout(() => this.refresh(), this.state.autoRefresh * 1000);
                        }
                    });
            })
        } else {
            let startDate = moment().subtract(9, 'day').format('MM/DD/YYYY');
            let endDate = moment().format('MM/DD/YYYY HH:mm');

            this.setState({
                startDate,
                endDate,
                loading: true
            }, () => {
                this.getDashMonitoring()
                    .then(() => {
                        this.setState({
                            loading: false
                        }, () => {
                            if (this.state.autoRefresh > 0) {
                                setTimeout(() => this.refresh(), this.state.autoRefresh * 1000);
                            }
                        })
                    })
                    .catch(error => {
                        if (this.state.autoRefresh > 0) {
                            setTimeout(() => this.refresh(), this.state.autoRefresh * 1000);
                        }
                    });
            });
        }
    }

    getData = () => {
        this.props.showLoading('Loading DASH monitoring data, one moment please...').then(() => {
            return this.getDashMonitoring();
        });
    }

    getDashMonitoring = () => {
        return this.props.getDashMonitoring(this.state.startDate, this.state.endDate)
            .then(response => {
                let { registrations, referrals, donorConnectRequests, donorConnectContactsMade, labOrders, labResults } = response.data.results;

                this.setState({
                    registrations,
                    referrals,
                    donorConnectRequests,
                    donorConnectContactsMade,
                    labOrders,
                    labResults
                }, () => {
                    this.props.hideLoading();
                });
            });
    }

    renderLegend = (props: any) => {
        const { payload } = props;

        return (
            <ul>
                {payload.map((entry, index) => (
                    <span key={`item-${index}`}>{entry.value}</span>
                ))}
            </ul>
        );
    }

    renderChart = (data: IDashMonitoringCount[], chartWidth: number) => {
        return (
            <div>
                <ResponsiveContainer width={'100%'} height={300}>
                    <ComposedChart
                        data={data}
                        margin={{
                            top: 30,
                            right: 30,
                            left: 20,
                            bottom: 5,
                        }}
                    >
                        <CartesianGrid strokeDasharray='3 3' />
                        <XAxis dataKey='monitorDateForDisplay' interval={0} tick={<CustomizedAxisTick />} />
                        <YAxis yAxisId='other' orientation='left' label={CustomizedLabelLeft} />
                        <YAxis yAxisId='plDash' orientation='right'><Label value='PL DASH' angle={-90} offset={15} dx={15} style={{ fill: '#a1a1a1' }} /></YAxis>
                        <Tooltip wrapperStyle={{ border: '1px solid #ddd', borderRadius: 4, outline: "none" }} />
                        <Legend wrapperStyle={{ position: 'relative', marginTop: '-20px' }} />
                        <Bar dataKey='micrositeCount' yAxisId='other' name='Microsites' stackId='a' fill='mediumblue' />
                        <Bar dataKey='nkrDashCount' yAxisId='other' name='NKR' stackId='a' fill='orange' />
                        <Line dataKey='plDashCount' yAxisId='plDash' name='PL DASH' type='monotone' stroke='#a1a1a1' />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        );
    }

    categoryTick = (props: any) => {
        return (
            <foreignObject x={props.x - 20} y={props.y} width={50} height={50}>
                <span style={{ textAlign: 'center' }}>{props.payload.value}</span>
            </foreignObject>
        );
    }

    render = () => {
        const marginTop = this.props.app.display == DisplayType.NoMenuNoTabs ? 10 : 0;
        const chartWidth = 700;

        return (
            <>
                <div className='container-fluid'>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: 30, marginBottom: 20, marginTop }}>
                        <div style={{ flexBasis: '100%', fontSize: 28, fontWeight: 500, display: 'flex', alignItems: 'center' }}>
                            DASH MONITORING
                        </div>
                        <div>{this.state.loading ? <span style={{ fontSize: 12, fontWeight: 400, paddingLeft: 30 }}><Glyphicon glyph='refresh' /></span> : ''}</div>
                    </div>

                    <Row>
                        <Col xs={12} lg={6}>
                            <div style={{ padding: 5, border: '1px solid #ddd', borderRadius: 4, marginBottom: 20 }}>
                                <div style={{ textAlign: 'center' }}>
                                    <h4>Registrations</h4>
                                </div>

                                {this.renderChart(this.state.registrations, chartWidth)}
                            </div>
                        </Col>

                        <Col xs={12} lg={6}>
                            <div style={{ padding: 5, border: '1px solid #ddd', borderRadius: 4, marginBottom: 20 }}>
                                <div style={{ textAlign: 'center' }}>
                                    <h4>Referrals</h4>
                                </div>

                                {this.renderChart(this.state.referrals, chartWidth)}
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12} lg={6}>
                            <div style={{ padding: 5, border: '1px solid #ddd', borderRadius: 4, marginBottom: 20 }}>
                                <div style={{ textAlign: 'center' }}>
                                    <h4>Donor Mentor Requests</h4>
                                </div>

                                {this.renderChart(this.state.donorConnectRequests, chartWidth)}
                            </div>
                        </Col>

                        <Col xs={12} lg={6}>
                            <div style={{ padding: 5, border: '1px solid #ddd', borderRadius: 4, marginBottom: 20 }}>
                                <div style={{ textAlign: 'center' }}>
                                    <h4>Donor Mentor Contacts Made</h4>
                                </div>

                                {this.renderChart(this.state.donorConnectContactsMade, chartWidth)}
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12} lg={6}>
                            <div style={{ padding: 5, border: '1px solid #ddd', borderRadius: 4 }}>
                                <div style={{ textAlign: 'center' }}>
                                    <h4>Lab Orders</h4>
                                </div>

                                {this.renderChart(this.state.labOrders, chartWidth)}
                            </div>
                        </Col>

                        <Col xs={12} lg={6}>
                            <div style={{ padding: 5, border: '1px solid #ddd', borderRadius: 4 }}>
                                <div style={{ textAlign: 'center' }}>
                                    <h4>Lab Results Made</h4>
                                </div>

                                {this.renderChart(this.state.labResults, chartWidth)}
                            </div>
                        </Col>
                    </Row>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, marginBottom: 10 }}>
                        <div style={{ flexBasis: '33%', display: 'flex', alignItems: 'center' }}>
                            <div style={{ marginRight: 5 }}>Show:</div>
                            <div style={{ marginRight: 10 }}>
                                <FormControlWrapper
                                    value={this.props.app.display}
                                    componentClass='select'
                                    style={{ fontSize: 12 }}
                                    onChange={this.handleDisplayChange}
                                >
                                    <option value='1'>Menus</option>
                                    <option value='4'>No Menus</option>
                                </FormControlWrapper>
                            </div>
                        </div>

                        <td style={{ flexBasis: '33%' }}>
                            {this.state.loading &&
                                <div className='alert alert-info' style={{ marginBottom: 0, paddingTop: 1, paddingBottom: 1, textAlign: 'center' }}>Updating...</div>
                            }
                        </td>

                        <div style={{ flexBasis: '33%', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                            <div style={{ marginRight: 5 }}>Auto refresh:</div>
                            <div style={{ marginRight: 5 }}>
                                <FormControlWrapper
                                    value={this.state.autoRefresh}
                                    componentClass='select'
                                    style={{ fontSize: 12 }}
                                    onChange={this.handleAutoRefreshChange}
                                >
                                    {refreshIntervals.map((interval, index) =>
                                        <option key={index} value={interval.value}>{interval.label}</option>
                                    )}
                                </FormControlWrapper>
                            </div>
                            <div>
                                <Glyphicon glyph='refresh' title='Refresh now' onClick={this.handleRefresh} />
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState, ownProps: IOwnProps): IStateProps => {
    return {
        app: appState.app
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getDashMonitoring: (startDate, endDate) => {
            return dispatch(ReportActionCreators.getDashMonitoring(startDate, endDate));
        },
        changeDisplay: (display) => {
            dispatch(AppActionCreators.changeDisplay(display));
        }
    };
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(DashMonitoring);